import type {CopilotSpace, CopilotSpacePayload, IndexCopilotSpace} from '@github-ui/copilot-chat-types'
import {announce} from '@github-ui/aria-live'
import {fetchCopilotSpace} from '@github-ui/copilot-shared-ui/spaces/fetch'
import {SpaceIcon} from '@primer/octicons-react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {ActionList, ActionMenu, Flash, Spinner} from '@primer/react'
import {use, useCallback, useState} from 'react'

import type {AhpSpaceSelection as QuickChatSpaceSelection} from '@github-ui/ahp-session-client/contracts'

import {useSpaces} from './use-spaces'

interface SpacePickerProps {
  selectedSpace: QuickChatSpaceSelection | undefined
  onSelectSpace: (space: QuickChatSpaceSelection | undefined) => void
  isResolving: boolean
  onResolvingChange: (isResolving: boolean) => void
  selectionError: string | undefined
  onSelectionErrorChange: (selectionError: string | undefined) => void
  disabled?: boolean
  disabledDescription?: string
  portalContainerName?: string
}

const NONE_KEY = 'none'

function spaceKey(space: Pick<IndexCopilotSpace, 'owner' | 'id'>): string {
  return `${space.owner}/${space.id}`
}

function isSpacePayload(payload: CopilotSpacePayload): payload is CopilotSpace {
  return 'generalInstructions' in payload
}

/**
 * Renders the space selector as a submenu inside the composer's attach ("+") menu.
 * Must be rendered as a child of an `ActionList` (e.g. via `ChatComposer`'s
 * `attachMenuContent` prop) so Primer wires up the nested-menu behavior.
 */
export function SpacePicker({
  selectedSpace,
  onSelectSpace,
  isResolving,
  onResolvingChange,
  selectionError,
  onSelectionErrorChange,
  disabled = false,
  disabledDescription,
  portalContainerName,
}: SpacePickerProps) {
  const {afterSelect: closeAttachMenu} = use(ActionList.ContainerContext)
  const [isOpen, setIsOpen] = useState(false)
  const {
    data: spaces,
    isLoading,
    isError,
  } = useSpaces({
    enabled: isOpen,
    onError: () => announce('Could not load spaces', {assertive: true}),
  })

  const handleSelect = async (space: IndexCopilotSpace) => {
    if (disabled || isResolving) return
    onSelectionErrorChange(undefined)
    onResolvingChange(true)
    announce(`Resolving space: ${space.name}`)
    try {
      const result = await fetchCopilotSpace({owner: space.owner, id: space.id})
      const payload = result.ok && isSpacePayload(result.payload) ? result.payload : undefined

      if (!payload?.slug) {
        const message =
          result.ok && 'protectedOrganizations' in result.payload
            ? 'Authorize SSO for this space, then try again.'
            : 'Could not select this space. Try again.'
        onSelectionErrorChange(message)
        announce(message, {assertive: true})
        return
      }

      onSelectSpace({
        owner: space.owner,
        id: space.id,
        slug: payload.slug,
        name: space.name,
      })
      announce(`Space selected: ${space.name}`)
      setIsOpen(false)
      closeAttachMenu?.()
    } catch {
      const message = 'Could not select this space. Try again.'
      onSelectionErrorChange(message)
      announce(message, {assertive: true})
    } finally {
      onResolvingChange(false)
    }
  }

  const handleClear = useCallback(() => {
    if (disabled || isResolving) return
    onSelectionErrorChange(undefined)
    onSelectSpace(undefined)
  }, [disabled, isResolving, onSelectSpace, onSelectionErrorChange])

  const selectedKey = selectedSpace ? spaceKey(selectedSpace) : NONE_KEY
  const buttonLabel = selectedSpace ? selectedSpace.name : 'No space'
  const isLocked = disabled || isResolving

  // `disabled` on the anchor `ActionList.Item` only sets ARIA/visual state — the
  // `ActionMenu.Anchor` still wires its own click/keyboard handlers, so guard the
  // open transition itself to keep a locked Space selection unreachable.
  const handleOpenChange = (next: boolean) => {
    if (next && isLocked) return
    setIsOpen(next)
  }

  return (
    <ActionMenu open={isOpen} onOpenChange={handleOpenChange}>
      <ActionMenu.Anchor>
        <ActionList.Item disabled={isLocked}>
          <ActionList.LeadingVisual>
            {isResolving ? <Spinner size="small" srText={null} /> : <SpaceIcon />}
          </ActionList.LeadingVisual>
          {`Space: ${isResolving ? 'Resolving…' : buttonLabel}`}
          {disabled && disabledDescription && <ActionList.Description>{disabledDescription}</ActionList.Description>}
        </ActionList.Item>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay
        width="medium"
        maxHeight="medium"
        overflow="auto"
        portalContainerName={portalContainerName}
        side="outside-right"
        align="end"
      >
        {selectionError && <Flash variant="danger">{selectionError}</Flash>}
        <ActionList selectionVariant="single">
          <ActionList.Item selected={selectedKey === NONE_KEY} disabled={isLocked} onSelect={handleClear}>
            No space
          </ActionList.Item>
          <ActionList.Divider />
          {isLoading ? (
            <ActionList.Item disabled>Loading spaces…</ActionList.Item>
          ) : isError ? (
            <ActionList.Item disabled>Could not load spaces</ActionList.Item>
          ) : spaces && spaces.length > 0 ? (
            spaces.map(space => (
              <ActionList.Item
                key={spaceKey(space)}
                selected={selectedKey === spaceKey(space)}
                disabled={isLocked}
                onSelect={event => {
                  event.preventDefault()
                  void handleSelect(space)
                }}
              >
                {space.name}
                <ActionList.Description>{space.owner}</ActionList.Description>
              </ActionList.Item>
            ))
          ) : (
            <ActionList.Item disabled>No spaces available</ActionList.Item>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
